import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import EmailAuth from './components/EmailAuth';
import VotingScreen from './components/VotingScreen';
import AlreadyVoted from './components/AlreadyVoted';
import CampaignEnded from './components/CampaignEnded';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeSwitcher from './components/ThemeSwitcher';
import Logo from './components/Logo';
import SignOutButton from './components/SignOutButton';
import { campaignAPI, userAPI } from './services/api';

function App() {
  const [step, setStep] = useState('auth'); // auth, loading, voting, alreadyVoted, campaignEnded
  const [campaign, setCampaign] = useState(null);
  const [votedAnimal, setVotedAnimal] = useState(null);
  const [error, setError] = useState('');
  const [noCampaign, setNoCampaign] = useState(false);

  const checkUserStatus = useCallback(async (campaignData) => {
    try {
      // Check if user has voted
      const voteStatus = await userAPI.checkIfVoted();
      
      if (voteStatus.hasVoted) {
        // Get user to find what they voted for
        const userData = await userAPI.getCurrentUser();
        const currentVote = userData.voteHistory.find(
          vote => vote.campaignId === campaignData._id
        );
        
        setVotedAnimal(currentVote?.chosenVote || 'Unknown');
        setStep('alreadyVoted');
      } else {
        setStep('voting');
      }
    } catch (err) {
      console.error('Error checking user status:', err);
      localStorage.removeItem('token');
      setStep('auth');
      // Silently discard 401 errors (expired/invalid token) — user just needs to log in again
      if (err.response?.status !== 401) {
        setError(err.response?.data?.error || 'Error loading campaign');
      }
    }
  }, []);

  const loadCampaign = useCallback(async () => {
    try {
      const campaignData = await campaignAPI.getCurrentCampaign();
      setCampaign(campaignData);

      // Check if campaign has ended (past its end date)
      if (campaignData.endDate && new Date(campaignData.endDate) < new Date()) {
        setStep('campaignEnded');
        return;
      }
      
      // Check if user is already authenticated
      const token = localStorage.getItem('token');
      if (token) {
        // Check user vote status directly
        checkUserStatus(campaignData);
      }
      // If no token, user is already at auth screen (default state)
    } catch (err) {
      console.error('Error loading campaign:', err);
      setNoCampaign(true);
      setStep('auth');
    }
  }, [checkUserStatus]);

  useEffect(() => {
    // Load campaign first
    loadCampaign();
  }, [loadCampaign]);

  const handleAuthenticated = (authData) => {
    // Token is already stored in localStorage by the API service
    // Check vote status directly
    if (campaign) {
      setStep('loading');
      checkUserStatus(campaign);
    } else {
      setStep('loading');
      loadCampaign();
    }
  };

  const handleVoteSubmitted = (animal) => {
    setVotedAnimal(animal);
    setStep('alreadyVoted');
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    setCampaign(null);
    setVotedAnimal(null);
    setError('');
    setStep('auth');
  };

  return (
    <div className="App">
      <LanguageSwitcher />
      <ThemeSwitcher />
      <Logo />
      {(step === 'voting' || step === 'alreadyVoted') && <SignOutButton onSignOut={handleSignOut} />}
      
      {step === 'auth' && <EmailAuth onAuthenticated={handleAuthenticated} noCampaign={noCampaign} />}
      
      {step === 'loading' && (
        <div className="loading-screen">
          <div className="loader"></div>
          <p>Carregando...</p>
        </div>
      )}
      
      {step === 'voting' && campaign && (
        <VotingScreen campaign={campaign} onVoteSubmitted={handleVoteSubmitted} />
      )}
      
      {step === 'alreadyVoted' && votedAnimal && (
        <AlreadyVoted votedAnimal={votedAnimal} />
      )}

      {step === 'campaignEnded' && (
        <CampaignEnded />
      )}
      
      {error && (
        <div className="error-overlay">
          <div className="error-message">{error}</div>
        </div>
      )}
    </div>
  );
}

export default App;
